import { useCallback } from 'react'
import { toast } from '@sales-center/components'
import type { FormikErrors } from 'formik'

import {
  type FormState,
  normalize,
  type NormalizedFormState,
} from '../formConfig'
import { ENTITY_TYPES } from '@sales-center/common'
import { useAppDispatch } from '@user/store/hooks'
import { updateLeadStatus } from '@user/store/lead'
import { isAPIError } from '@user/store/services/crud'
import { updateOpportunityStatus } from '@user/store/opportunity'
import { getAllEntityAppointments } from '@user/store/scheduling'
import { ISalesFunnelClosedReasons } from '@user/store/salesFunnel'
import { getForms } from '@user/store/agreements/api'

type UpdateStatusFunc = (model: NormalizedFormState) => Promise<{
  success: boolean
  message: string
}>

type Props = {
  entityId: number
  onClose: () => void
  entityType: ENTITY_TYPES
  salesFunnelStageId: number
  requireCancelReason: boolean
  assigneeId: string | null
  reasonDetail: ISalesFunnelClosedReasons
}

const SERVICE_ORDER_POSTED_ERROR =
  'Service Order already posted as complete. It cannot be posted again.'

export function useHandlers(props: Props) {
  const {
    onClose,
    entityId,
    entityType,
    salesFunnelStageId,
    requireCancelReason,
    reasonDetail: { disqualifyReasons },
    assigneeId,
  } = props

  const dispatch = useAppDispatch()

  const getReasonLabel = useCallback(
    (id: number) => {
      return disqualifyReasons.find((x) => x.id == id)?.reason
    },
    [disqualifyReasons],
  )

  const updateLead = useCallback<UpdateStatusFunc>(
    async (model) => {
      const { reasonId, customReason, notServicedReason } = model

      const result = await dispatch(
        updateLeadStatus({
          reasonId,
          customReason:
            reasonId == 0 ? customReason : getReasonLabel(reasonId)!,
          leadId: entityId,
          notServicedReason,
          salesFunnelStageId,
          disqualifiedDate: null,
          salesFunnelStageName: '',
          owner: assigneeId,
        }),
      )
      if (updateLeadStatus.fulfilled.match(result)) {
        dispatch(
          getForms({
            externalEntityId: entityId + '',
            externalEntityType: entityType,
          }),
        )

        return {
          success: true,
          message: 'Lead Disqualified',
        }
      } else if (isAPIError(result.payload)) {
        if (result.payload.Message?.includes(SERVICE_ORDER_POSTED_ERROR)) {
          return {
            success: true,
            message: 'Lead Disqualified',
          }
        }
        return {
          success: false,
          message: result.payload.Message,
        }
      } else {
        return {
          success: false,
          message: 'Unable to disqualify Lead',
        }
      }
    },
    [
      dispatch,
      getReasonLabel,
      entityId,
      salesFunnelStageId,
      assigneeId,
      entityType,
    ],
  )

  const updateOpp = useCallback<UpdateStatusFunc>(
    async (model) => {
      const { reasonId, customReason, notServicedReason, selectedServiceIds } =
        model

      const result = await dispatch(
        updateOpportunityStatus({
          reasonId,
          customReason:
            reasonId == 0 ? customReason : getReasonLabel(reasonId)!,
          notServicedReason,
          salesFunnelStageId,
          opportunityId: entityId,
          serviceIds:
            selectedServiceIds.length > 0
              ? selectedServiceIds.map(Number)
              : undefined,
        }),
      )

      if (updateOpportunityStatus.fulfilled.match(result)) {
        dispatch(
          getForms({
            externalEntityId: entityId + '',
            externalEntityType: entityType,
          }),
        )

        return {
          success: true,
          message: 'Opportunity Closed',
        }
      } else if (isAPIError(result.payload)) {
        if (result.payload.Message?.includes(SERVICE_ORDER_POSTED_ERROR)) {
          return {
            success: true,
            message: 'Opportunity Closed',
          }
        }
        return {
          success: false,
          message: result.payload.Message,
        }
      } else {
        return {
          success: false,
          message: 'Unable to close Opportunity',
        }
      }
    },
    [dispatch, entityId, salesFunnelStageId, getReasonLabel, entityType],
  )

  const submitHandler = useCallback(
    async (model: FormState) => {
      const norm = normalize(model)

      const { success, message } =
        entityType === ENTITY_TYPES.Lead
          ? await updateLead(norm)
          : await updateOpp(norm)

      if (success) {
        if (norm.notServicedReason) {
          dispatch(
            getAllEntityAppointments({
              entityId,
              entityType,
            }),
          )
        }

        toast.success(message)
        onClose()
      } else {
        toast.error(message)
      }
    },
    [dispatch, entityId, entityType, onClose, updateLead, updateOpp],
  )

  const validate = useCallback(
    (model: FormState) => {
      const {
        reasonId,
        customReason,
        notServicedReason,
        selectedServiceIds,
        cancelConfirmation,
      } = model

      const errors: FormikErrors<FormState> = {}

      if (reasonId === '') errors.reasonId = 'Required'

      if (reasonId === 'custom' && customReason.trim().length === 0)
        errors.customReason = 'Required'

      if (requireCancelReason && notServicedReason === '')
        errors.notServicedReason = 'Required'

      if (
        selectedServiceIds.length > 0 &&
        cancelConfirmation.trim().toUpperCase() !== 'CANCEL'
      )
        errors.cancelConfirmation = 'Required'

      return errors
    },
    [requireCancelReason],
  )

  return {
    validate,
    submitHandler,
  }
}
